import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { tap } from 'rxjs/operators';

import { environment } from "../../environments/environment";
import { Logger } from "../global/logger";
import { WebServiceResponse } from "../global/responses/web-service-response";
import { Quote } from "./quote";
import { DateTimeParser } from "../global/date-time-parser";
import { ChargeTypes } from "../cfm-data/charge-types";
import { QuoteRate } from "./quote-rate";
import { QuoteContact } from "./quote-contact";
import { GetObjectResponse } from "../global/responses/get-object-response";
import { ExecuteObservable } from '../global/execute-observable';
import { UpdateQuoteInfo } from '../global/responses/update-quote-info';
import { RateQuoteInfo } from '../global/responses/rate-quote-info';

/**
 * This class is the service used to communicate quote information to/from the web service.
 */
@Injectable()
export class QuotingService {

// #region [ Fields ]

	/** The path to the quote controller. */
	private static readonly QuoteControllerPath: string = "Quote/";

	/** Stores the quote being entered by the user. */
	private static _Quote: Quote;

	/** The Crown ID currently selected by the user.  This value will be null if no selection was made or the session Crown ID hasn't been downloaded. */
	private _CrownIdentifier: string = null;

// #region [ Constructor ]

	constructor(
		private _HttpClient: HttpClient
	) {
		QuotingService._Quote = null;
	}

// #region [ Properties ]

	/**
	 * Indicates if the quote is currently loaded from the web server session state.
	 *
	 * @readonly
	 */
	public get IsQuoteInMemory(): boolean {
		return (QuotingService._Quote != null);
	}

	/**
	 * The quote data saved in the service.  This data is updated whenever the session state is loaded or updated.
	 *
	 * @readonly
	 */
	public get Quote(): Quote {
		return QuotingService._Quote;
	}

	public get CrownIdentifier(): string {
		return this._CrownIdentifier;
	}

// #region [ Public Methods ]

	/**
	 * Clears all state from the service so that data needs to be reloaded.
	 */
	public ClearState(): void {
		this._CrownIdentifier = null;
		this.ClearQuote();
	}

	/**
	 * Clears the quote that's saved in memory.  Does not update the session state on the web server.
	 */
	public ClearQuote(): void {
		QuotingService._Quote = new Quote();
		QuotingService._Quote.Shipper = new QuoteContact();
		QuotingService._Quote.Consignee = new QuoteContact();
	}

	/**
	 * Gets the Crown ID of the current session from the session state.
	 *
	 * @returns A promise that supplies the Crown ID of the current session.
	 */
	public GetCrownIdentifier(): Promise<string> {
		let objObservable: Observable<GetObjectResponse<string>>;
		let strUrl: string;
		let intIdentifier: number;

		// Build the request.
		strUrl = environment.WebServiceUrl + QuotingService.QuoteControllerPath + "GetCrownIdentifier";
		objObservable = this._HttpClient.get<GetObjectResponse<string>>(strUrl, { withCredentials: true });

		// Setup logging.
		intIdentifier = Logger.ServiceStartLog("GET", strUrl, null);
		objObservable = objObservable.pipe(tap(
			(objResponse: GetObjectResponse<string>) => {
				Logger.ServiceEndLog(intIdentifier, "GET", strUrl, objResponse);
				if (objResponse.OperationSucceeded)
					this._CrownIdentifier = objResponse.Result;
			}
		));

		return ExecuteObservable.ExecuteQuery<string>(objObservable, "QuotingService.GetCrownIdentifier");
	}

	/**
	 * Sets the Crown ID of the current session from the session state.
	 * 
	 * @param CrownIdentifier The Crown ID being set for the session.
	 * @returns A promise which sets the Crown ID of the current session.
	 */
	public SetCrownIdentifier(CrownIdentifier: string): Promise<void> {
		let objObservable: Observable<WebServiceResponse>;
		let strUrl: string;
		let intIdentifier: number;

		// Set the current service state.
		this._CrownIdentifier = CrownIdentifier;

		// Build the request.
		strUrl = environment.WebServiceUrl + QuotingService.QuoteControllerPath + `SetCrownIdentifier/${CrownIdentifier}`;
		objObservable = this._HttpClient.put<WebServiceResponse>(strUrl, null, { withCredentials: true });

		// Setup logging.
		intIdentifier = Logger.ServiceStartLog("PUT", strUrl, null);
		objObservable = objObservable.pipe(tap(
			(objResponse: WebServiceResponse) => {
				Logger.ServiceEndLog(intIdentifier, "PUT", strUrl, objResponse);
			}
		));

		return ExecuteObservable.ExecuteVoid(objObservable, "QuotingService.SetCrownIdentifier");
	}

	/**
	 * Returns the Crown ID already loaded in memory or gets it from the server if it hasn't been previously loaded.
	 *
	 * @returns A promise that supplies the Crown ID.
	 */
	public LoadCrownIdentifier(): Promise<string> {
		return new Promise((objResolve: Function, objReject) => {
			if (this.CrownIdentifier == null) {
				this.GetCrownIdentifier()
					.then((strCrownIdentifier: string) => objResolve(strCrownIdentifier))
					.catch(() => objReject());
			}
			else
				objResolve(this.CrownIdentifier);
		});
	}

	/**
	 * Loads the quote from the session state on the web server.
	 *
	 * @returns A promise that supplies the current session quote.
	 */
	public LoadQuoteFromSessionState(): Promise<Quote> {
		let objObservable: Observable<GetObjectResponse<Quote>>;
		let strUrl: string;
		let intIdentifier: number;

		// Build the request.
		strUrl = environment.WebServiceUrl + QuotingService.QuoteControllerPath + "GetQuote/";
		objObservable = this._HttpClient.get<GetObjectResponse<Quote>>(strUrl, { withCredentials: true }).pipe(tap(
			(objResponse: GetObjectResponse<Quote>) => {
				if (objResponse.OperationSucceeded)
					this.ProcessQuoteData(objResponse.Result);
				else
					QuotingService._Quote = new Quote();
			}
		));

		// Setup logging.
		intIdentifier = Logger.ServiceStartLog("GET", strUrl, null);
		objObservable = objObservable.pipe(tap(
			(objResponse: GetObjectResponse<Quote>) => Logger.ServiceEndLog(intIdentifier, "GET", strUrl, objResponse)
		));

		return ExecuteObservable.ExecuteQuery<Quote>(objObservable, "QuotingService.LoadQuoteFromSessionState");
	}

	/**
	 * Updates the quote stored in the session state.  Only the fields that are included in the step specified in the Step parameter are updated.
	 *
	 * @param		Step	The step in the quote entry process.  Only the fields associated with that step will be updated.
	 * @returns		A promise that supplies data associated with updating a quote.
	 */
	public UpdateSessionQuote(): Promise<UpdateQuoteInfo> {
		let objObservable: Observable<GetObjectResponse<UpdateQuoteInfo>>;
		let strUrl: string;
		let intIdentifier: number;

		// Build the request.
		strUrl = environment.WebServiceUrl + QuotingService.QuoteControllerPath + "UpdateQuote/";
		objObservable = this._HttpClient.put<GetObjectResponse<UpdateQuoteInfo>>(strUrl, QuotingService._Quote, { withCredentials: true });

		// Post-processing and setup logging.
		intIdentifier = Logger.ServiceStartLog("PUT", strUrl, QuotingService._Quote);
		objObservable = objObservable.pipe(tap(
			(objResponse: GetObjectResponse<UpdateQuoteInfo>) => {
				Logger.ServiceEndLog(intIdentifier, "PUT", strUrl, objResponse);
				if (QuotingService._Quote.Shipper != null)
					QuotingService._Quote.Shipper.Changed = false;
				if (QuotingService._Quote.Consignee != null)
					QuotingService._Quote.Consignee.Changed = false;
			}
		));

		return ExecuteObservable.ExecuteQuery<UpdateQuoteInfo>(objObservable, "QuotingService.UpdateSessionQuote");
	}

	/**
	 * Loads a quote from the database.
	 *
	 * @param CrownIdentifier The Crown ID of the quote to load.
	 * @param ShipID The unique shipment identifier of the quote to load.
	 * @returns A promise that supplies the quote.
	 */
	public LoadQuote(CrownIdentifier: string, ShipID: number): Promise<Quote> {
		let objObservable: Observable<GetObjectResponse<Quote>>;
		let strUrl: string;
		let intIdentifier: number;

		// Build the request.
		strUrl = environment.WebServiceUrl + QuotingService.QuoteControllerPath + `LoadQuote/${CrownIdentifier}/${ShipID}`;
		objObservable = this._HttpClient.get<GetObjectResponse<Quote>>(strUrl, { withCredentials: true });

		// Post-processing and setup logging.
		intIdentifier = Logger.ServiceStartLog("GET", strUrl, null);
		objObservable = objObservable.pipe(tap(
			(objResponse: GetObjectResponse<Quote>) => {
				Logger.ServiceEndLog(intIdentifier, "GET", strUrl, objResponse);
				if (objResponse.OperationSucceeded)
					this.ProcessQuoteData(objResponse.Result);
				else
					QuotingService._Quote = new Quote();
			}
		));

		return ExecuteObservable.ExecuteQuery<Quote>(objObservable, "QuotingService.LoadQuote");
	}

	/**
	 * Gets a rating for and saves the session quote and saves it to CFM.
	 *	
	 * @param IsQuickQuote Indicates if this request is associated with a quick quote for EDI point charging.
	 * @returns A promise that supplies rating information.
	 */
	public RateAndSaveQuote(IsQuickQuote: boolean): Promise<RateQuoteInfo> {
		let objObservable: Observable<GetObjectResponse<RateQuoteInfo>>;
		let strUrl: string;
		let intIdentifier: number;

		// Build the request.
		strUrl = environment.WebServiceUrl + QuotingService.QuoteControllerPath + `RateAndSaveQuote/${IsQuickQuote}`;
		objObservable = this._HttpClient.get<GetObjectResponse<RateQuoteInfo>>(strUrl, { withCredentials: true });

		// Post-processing and setup logging.
		intIdentifier = Logger.ServiceStartLog("GET", strUrl, null);
		objObservable = objObservable.pipe(tap(
			(objResponse: GetObjectResponse<RateQuoteInfo>) => {
				if (objResponse.OperationSucceeded)
					this.ProcessQuoteData(objResponse.Result.Quote);
				Logger.ServiceEndLog(intIdentifier, "GET", strUrl, objResponse);
			}
		));

		return ExecuteObservable.ExecuteQuery<RateQuoteInfo>(objObservable, "QuotingService.RateAndSaveQuote");
	}

	/**
	 * Gets a rating for the session quote and saves it to CFM.  Only use this for quick quoting - it will charge EDI points.
	 *
	 * @param CrownIdentifier The Crown ID of the company to rate the quote with.
	 * @param Quote The quote to rate.
	 * @returns A promise that supplies rating information.
	 */
	public RateQuote(CrownIdentifier: string, Quote: Quote): Promise<RateQuoteInfo> {
		let objObservable: Observable<GetObjectResponse<RateQuoteInfo>>;
		let strUrl: string;
		let intIdentifier: number;

		// Build the request.
		strUrl = environment.WebServiceUrl + QuotingService.QuoteControllerPath + `RateQuote/${CrownIdentifier}/true`;
		objObservable = this._HttpClient.post<GetObjectResponse<RateQuoteInfo>>(strUrl, Quote, { withCredentials: true });

		// Setup logging.
		intIdentifier = Logger.ServiceStartLog("POST", strUrl, Quote);
		objObservable = objObservable.pipe(tap(
			(objResponse: GetObjectResponse<RateQuoteInfo>) => Logger.ServiceEndLog(intIdentifier, "POST", strUrl, objResponse)
		));

		return ExecuteObservable.ExecuteQuery<RateQuoteInfo>(objObservable, "QuotingService.RateQuote");
	}

	/**
	 * Deletes the quote stored in session state on the server.
	 *
	 * @returns A promise that deletes the quote from the session state.
	 */
	public DeleteQuote(): Promise<void> {
		let objObservable: Observable<WebServiceResponse>;
		let strUrl: string;
		let intIdentifier: number;

		// Build the request.
		strUrl = environment.WebServiceUrl + QuotingService.QuoteControllerPath + "DeleteQuote";
		objObservable = this._HttpClient.delete<WebServiceResponse>(strUrl, { withCredentials: true });

		// Post-processing and setup logging.
		intIdentifier = Logger.ServiceStartLog("DELETE", strUrl, null);
		objObservable = objObservable.pipe(tap(
			(objResponse: WebServiceResponse) => {
				if (objResponse.OperationSucceeded)
					this.ClearQuote();
				Logger.ServiceEndLog(intIdentifier, "DELETE", strUrl, objResponse);
			}
		));

		return ExecuteObservable.ExecuteVoid(objObservable, "QuotingService.DeleteQuote");
	}

	/**
	 * Books a shipment that is currently a quote. This will charge EDI points.
	 *
	 * @returns A promise that books a quote as a shipment.
	 */
	public BookQuote(): Promise<void> {
		let objObservable: Observable<WebServiceResponse>;
		let strUrl: string;
		let intIdentifier: number;

		// Build the request.
		strUrl = environment.WebServiceUrl + QuotingService.QuoteControllerPath + "BookQuote";
		objObservable = this._HttpClient.put<WebServiceResponse>(strUrl, null, { withCredentials: true });

		// Post-processing and setup logging.
		intIdentifier = Logger.ServiceStartLog("PUT", strUrl, null);
		objObservable = objObservable.pipe(tap(
			(objResponse: WebServiceResponse) => {
				if (objResponse.OperationSucceeded)
					this.ClearQuote();
				Logger.ServiceEndLog(intIdentifier, "PUT", strUrl, objResponse);
			}
		));

		return ExecuteObservable.ExecuteVoid(objObservable, "QuotingService.BookQuote");
	}

// #region [ Private Methods ]

	/**
	 * Adjusts the quote data from the web service to what's required for this app.
	 *
	 * @param ResponseQuote The quote from the web service response as the JSON serializer creates it.
	 */
	private ProcessQuoteData(ResponseQuote: Quote): void {
		let objSurcharges: QuoteRate[];

		QuotingService._Quote = ResponseQuote;
		if (QuotingService._Quote.PickupDate != null && typeof QuotingService._Quote.PickupDate == "string")
			QuotingService._Quote.PickupDate = DateTimeParser.ParseStringDate(QuotingService._Quote.PickupDate);
		if (QuotingService._Quote.DeliveryDate != null && typeof QuotingService._Quote.DeliveryDate == "string")
			QuotingService._Quote.DeliveryDate = DateTimeParser.ParseStringDate(QuotingService._Quote.DeliveryDate);
		if (QuotingService._Quote.Surcharges != null) {
			objSurcharges = new Array<QuoteRate>();
			for (let objType of QuotingService._Quote.Surcharges) {
				switch (objType.Type.Value) {
					case ChargeTypes.Pickup.Value:
						objType.Type = ChargeTypes.Pickup;
						break;
					case ChargeTypes.Delivery.Value:
						objType.Type = ChargeTypes.Delivery;
						break;
					case ChargeTypes.LineHaul.Value:
						objType.Type = ChargeTypes.LineHaul;
						break;
					case ChargeTypes.Transfer.Value:
						objType.Type = ChargeTypes.Transfer;
						break;
				}
				objSurcharges.push(objType);
				QuotingService._Quote.Surcharges = objSurcharges;
			}
		}
	}

}